import { Hero } from "@/components/Hero";
import { useCanonicalTag } from "@/hooks/useCanonicalTag";

import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Check, Briefcase, Target, MessageCircle, Heart, Lock, FileText } from "lucide-react";
import heroImage from "@/assets/hero-chillwerk.png";
import cvFreeDynamic from "@/assets/cv-free-dynamic.png";
import cvFreeHero from "@/assets/cv-free-hero.png";
import adventureHero from "@/assets/adventure-hero.png";
import logo from "@/assets/logo-chillwerk.png";
import logoChillwerk from "@/assets/logo-chillwerk.png";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageErrorBoundary } from "@/components/PageErrorBoundary";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RadarSection } from "@/components/RadarSection";
import { RadarStepsSection } from "@/components/RadarStepsSection";
import { UGCVideoSection } from "@/components/UGCVideoSection";
import { HomepageJobsSection } from "@/components/HomepageJobsSection";
import { ChillExplainer } from "@/components/ChillExplainer";
import { SocialReelsSection } from "@/components/SocialReelsSection";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { MatchesCounterSection } from "@/components/MatchesCounterSection";
import { useTikTokPixel } from "@/hooks/useTikTokPixel";

const Index = () => {
  const navigate = useNavigate();
  const { trackFunnelEvent } = useVisitorTracking();
  const { trackPageView: trackTikTokPageView } = useTikTokPixel();

  useEffect(() => {
    trackTikTokPageView();
  }, [trackTikTokPageView]);

  const handleCTAClick = (ctaType: string) => {
    trackFunnelEvent('homepage_cta_click', { cta_type: ctaType });
    navigate('/auth');
  };

  // Fetch homepage sections configuration
  const { data: sectionConfig } = useQuery({
    queryKey: ['homepage-sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dashboard_tiles')
        .select('tile_key, is_visible')
        .eq('dashboard_type', 'homepage')
        .eq('is_visible', true);

      if (error) {
        console.error('Error fetching section config:', error);
        return {};
      }

      // Convert to map for easy lookup
      return data.reduce((acc, item) => {
        acc[item.tile_key] = item.is_visible;
        return acc;
      }, {} as Record<string, boolean>);
    },
  });

  // Helper function to check if section should be visible
  // Default to TRUE while config loads, so sections render immediately (no layout shift / glitch).
  // Admin toggles only hide a section once we have confirmation it's set to false.
  const isSectionVisible = (key: string) => {
    if (!sectionConfig) return true;
    return sectionConfig[key] !== false;
  };


  useCanonicalTag('/');

  // Add Organization schema for SEO
  useEffect(() => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Chillwerk",
      "alternateName": "Chillwerk.nl",
      "url": baseUrl,
      "logo": `${baseUrl}${logo}`,
      "description": "De nieuwe generatie werk. Swipe door vacatures, ontdek bedrijven en vind werk op jouw manier. Weg met het cv, hallo menselijk contact.",
      "foundingDate": "2024",
      "slogan": "Werk zoeken. Maar dan chill.",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "connect@chillwerk.nl"
      },
      "sameAs": ["https://www.linkedin.com/company/chillwerk", "https://twitter.com/chillwerk", "https://www.instagram.com/chillwerk"],
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "NL"
      },
      "areaServed": {
        "@type": "Country",
        "name": "Netherlands"
      },
      "knowsAbout": ["Recruitment", "Job Matching", "Career Development", "Employer Branding"],
      "serviceType": ["Job Board", "Recruitment Platform", "Career Matching"]
    };

    // Create and inject script tag
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(organizationSchema);
    script.id = 'organization-structured-data';

    // Remove existing script if present
    const existingScript = document.getElementById('organization-structured-data');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    // Cleanup
    return () => {
      const scriptToRemove = document.getElementById('organization-structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);
  
  return (
    <PageErrorBoundary
      fallbackTitle="Oeps! Er ging iets mis"
      fallbackMessage="De homepage kon niet worden geladen. Probeer de pagina te verversen."
    >
      <div className="min-h-screen bg-background overflow-x-clip">
        <Navigation />
      
      <main>
        {isSectionVisible('hero') && <Hero />}
        
        {/* Matches counter */}
        <MatchesCounterSection />
        



        
        {/* Radar Section - Zet jezelf op de radar */}
        {isSectionVisible('radar') && <RadarSection />}

        {/* 3 stappen + uitnodiging */}
        <RadarStepsSection />

        {/* Featured Jobs Section - altijd zichtbaar, geen toggle-flits */}
        <HomepageJobsSection />

        {/* UGC Video Section */}
        <UGCVideoSection />
        
        {/* Chill Explainer Section */}
        <ChillExplainer />
        
        {/* How It Works Section */}

        {/* Social Reels Section */}
        <SocialReelsSection />
        
        




        {/* Final CTA Section */}
        <section className="py-24 md:py-32 relative overflow-hidden bg-[hsl(194_78%_16%)]">
          {/* Diepte-accenten */}
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(194_80%_13%)] via-[hsl(193_72%_19%)] to-[hsl(190_70%_14%)] pointer-events-none" />
          <div className="absolute -top-20 left-[10%] w-[30rem] h-[30rem] bg-cyan/20 rounded-full blur-[150px] pointer-events-none" />
          <div className="absolute bottom-[-10%] right-[8%] w-[24rem] h-[24rem] bg-yellow/10 rounded-full blur-[140px] pointer-events-none" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <div className="inline-block px-4 py-2 bg-yellow text-yellow-foreground rounded-full font-bold text-sm mb-6">
              🚀 Start vandaag nog
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Klaar om te starten?
            </h2>
            <p className="text-lg md:text-xl text-white/75 mb-12 max-w-2xl mx-auto">
              Voor de nieuwe generatie werkzoekenden en werkgevers. Binnen 1 minuut geregeld.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto px-2">
              <Button
                onClick={() => navigate('/signup/kandidaat?src=slot-cta')}
                variant="cta"
                size="lg"
                className="w-full sm:flex-1"
              >
                Ik zoek werk
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
              <Button
                onClick={() => navigate('/start/bedrijven')}
                size="lg"
                className="w-full sm:flex-1 bg-white/10 text-white hover:bg-white/20 border border-white/25 backdrop-blur-sm"
              >
                Ik zoek talent
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </div>
          </div>
        </section>

      </main>
      
      <Footer />
    </div>
    </PageErrorBoundary>
  );
};
export default Index;